import React from "react";
import ProfileImage from "../profileImage";

const ChatListItem = ({ chat, onClick, type = 'private' }) => {
  // Format time display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Get display info based on chat type
  const getDisplayInfo = () => {
    if (type === 'organization') {
      return {
        title: chat.name || 'Organization Chat',
        subtitle: chat.description || `${chat.participantCount} members`,
        image: chat.logo,
        showPremium: false,
        roomType: chat.roomType,
        teamInfo: chat.teamInfo
      };
    } else {
      return {
        title: chat.receiverName || `${chat.receiverFirstName || ''} ${chat.receiverLastName || ''}`.trim() || 'Unknown User',
        subtitle: `@${chat.receiverUserName || 'unknown'}`,
        image: chat.logo || chat.receiverProfilePicture,
        showPremium: chat.isPremium,
        accountType: chat.receiverAccountType
      };
    }
  };

  const displayInfo = getDisplayInfo();
  const lastMessageTime = chat.lastActivity || chat.lastMessageDate || chat.updatedAt;

  return (
    <div
      onClick={() => onClick(chat)}
      className="block hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
    >
      <div className="p-4">
        <div className="flex items-start space-x-4">
          {/* Profile/Room Image */}
          <div className="relative flex-shrink-0">
            <ProfileImage
              image={displayInfo.image}
              user={type === 'private' ? { profilePicture: chat.receiverProfilePicture, _id: chat.receiverId } : null}
              alt={displayInfo.title}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
            />
            
            {/* Unread Messages Badge */}
            {(chat.unreadCount || chat.unreadMessages) > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                {(chat.unreadCount || chat.unreadMessages) > 99 ? '99+' : (chat.unreadCount || chat.unreadMessages)}
              </span>
            )}

            {/* Room Type Indicator for Organization Chats */}
            {type === 'organization' && displayInfo.roomType && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center border-2 border-gray-100">
                {displayInfo.roomType === 'team' && (
                  <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                )}
                {displayInfo.roomType === 'announcement' && (
                  <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
                {displayInfo.roomType === 'general' && (
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            )}
          </div>

          {/* Chat Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {displayInfo.title}
                </h3>
                
                {/* Premium Badge for Private Chats */}
                {displayInfo.showPremium && (
                  <span
                    className="text-yellow-500 animate-pulse flex-shrink-0"
                    title="Premium User"
                  >
                    👑
                  </span>
                )}

                {/* Room Type Badge for Organization Chats */}
                {type === 'organization' && displayInfo.roomType && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                    displayInfo.roomType === 'team' ? 'bg-blue-100 text-blue-800' :
                    displayInfo.roomType === 'announcement' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {displayInfo.roomType}
                  </span>
                )}
              </div>
              
              {/* Time */}
              <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                {lastMessageTime && formatTime(lastMessageTime)}
              </span>
            </div>

            {/* Subtitle */}
            <p className="text-xs text-gray-500 truncate mt-1">
              {displayInfo.subtitle}
            </p>

            {/* Team Info for Team Rooms */}
            {displayInfo.teamInfo && (
              <p className="text-xs text-blue-600 truncate mt-1">
                Team: {displayInfo.teamInfo.name}
              </p>
            )}

            {/* Last Message */}
            {chat.lastMessage && (
              <div className="mt-2 flex items-center space-x-2">
                <p className="text-sm text-gray-600 flex-1 truncate">
                  {chat.lastMessage}
                </p>
                
                {/* Last Message Creator for Private Chats */}
                {type === 'private' && chat.lastMessageCreatorProfilePicture && (
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <ProfileImage
                      image={chat.lastMessageCreatorProfilePicture}
                      user={{ profilePicture: chat.lastMessageCreatorProfilePicture, _id: chat.lastMessageCreatorId }}
                      alt={`${chat.lastMessageCreatorFirstName || ''} ${chat.lastMessageCreatorLastName || ''}`.trim() || 'User'}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-xs text-gray-500">
                      {chat.lastMessageCreatorFirstName || 'User'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Participant Count for Organization Chats */}
            {type === 'organization' && chat.participantCount > 0 && (
              <div className="mt-2 flex items-center space-x-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-xs text-gray-500">
                  {chat.participantCount} {chat.participantCount === 1 ? 'member' : 'members'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatListItem; 